import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { map, Observable } from 'rxjs';

/**
 * PA-facing client for the connect-service review-thread endpoints (Task 8).
 * A review thread hangs on the Task a connect vendor is assigned to; internal
 * staff (Order Tracker) and the PA (CONNECT) hold a conversation on it. The PA
 * may only ever open a QUESTION thread — the server FORCES `type = QUESTION`
 * and would reject a revision-type create — and may resolve their own
 * questions; a REVISION_REQUIRED thread is closed by the review team only (the
 * resolve endpoint 403s for the PA on those).
 *
 * All three endpoints hang off the `/proposal/review-thread` base and return
 * the shared ApiResponse envelope (`{ status, message, data }`); each method
 * unwraps `.data`, converting a FAILURE envelope into an error notification so
 * a single subscriber `error` path can keep the caller's draft and toast.
 *
 * Auth: the bearer token is attached per-call, matching every other service in
 * this app (`ProposalService`/`DocumentService`/`AdminService`) — the
 * `TokenInterceptor` is declared but not actually wired into the root
 * HttpClient (see `AdminService`'s note), so a request without this header
 * would be unauthenticated.
 */
@Injectable({
  providedIn: 'root',
})
export class ReviewThreadService {
  private tokenKey = 'token';
  private baseUrl = environment.apiUrl + '/proposal/review-thread';

  constructor(private http: HttpClient) {}

  private jsonHeaders(): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${localStorage.getItem(this.tokenKey)}`,
      'Content-Type': 'application/json',
    });
  }

  // Unwraps the ApiResponse envelope to its `.data`. A FAILURE envelope is
  // thrown so the caller's `error` callback (which keeps the draft + toasts)
  // handles it uniformly with a transport failure.
  //
  // NOTE: this throws a plain `Error`, not an `ApiFailureError`. Today's only
  // consumer (CommentsCardComponent) ignores the thrown value and toasts its own
  // fixed copy, so nothing is lost. But a future consumer that wired this to
  // `serverErrorMessage` would silently get the generic fallback every time —
  // that helper treats only an `ApiFailureError` as carrying server-authored,
  // showable copy. Switch this to `ApiFailureError` before relying on the
  // envelope's message reaching a toast.
  private unwrap = (response: any): any => {
    if (response?.status === 'FAILURE') {
      throw new Error(response?.message || 'An error occurred. Please try again.');
    }
    return response?.data ?? null;
  };

  // Creates a QUESTION thread on the vendor's task for `siteId`. The server
  // forces `type = QUESTION`, mints the thread + first VENDOR message, and
  // returns the created thread as `.data`.
  createQuestionThread(siteId: string, body: string): Observable<any> {
    return this.http
      .post<any>(this.baseUrl, JSON.stringify({ siteId, body }), {
        headers: this.jsonHeaders(),
        responseType: 'json',
      })
      .pipe(map(this.unwrap));
  }

  // Appends a VENDOR message (server-set sender) to an existing thread.
  postMessage(threadId: string, body: string): Observable<any> {
    return this.http
      .post<any>(`${this.baseUrl}/${threadId}/message`, JSON.stringify({ body }), {
        headers: this.jsonHeaders(),
        responseType: 'json',
      })
      .pipe(map(this.unwrap));
  }

  // Resolves the PA's own QUESTION thread. The server rejects (403) if the
  // thread is REVISION_REQUIRED — those are closed by the review team only.
  resolveThread(threadId: string): Observable<any> {
    return this.http
      .put<any>(`${this.baseUrl}/${threadId}/resolve`, {}, {
        headers: this.jsonHeaders(),
        responseType: 'json',
      })
      .pipe(map(this.unwrap));
  }
}
